import React, {useEffect, useMemo, useState} from 'react';
import {fetchApi} from "../../../../../../services/HTTP";
import VStack from "../../../../../common/Layout/VStack";
import {t} from "../../../../../../services/i18n";
import AdvanceSelect from "../../../../../common/AdvanceSelect";
import FormInput from "../../../../../common/FormInput";
import CustomCheckbox from "../../../../../common/CustomCheckbox";
import {checkRequired} from "../../../../../../services/stepperService";
import {useSelector} from "react-redux";
import {CLASS, GROUP_FORM, HUGIM_FORM, APPOINTMENT_FORM,APPOINTMENT} from "../../../../../../services/ConstHelper";
import moment from "moment";
import {dbDateFormat} from "../../../../../../services/Helpers";
import {Tooltip} from "antd";
import {Colors} from "../../../../../../styles/Colors";

const sectionCardProps = {space: '0.75rem', p: '1rem', bg: Colors.newDesign.menuBar, borderWidth: 1, borderStyle: 'solid', borderColor: Colors.slotBorder, borderRadius: '12px', boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.04)'};

const FlowForm = (props) => {
    const { values, setFieldValue, errors, touched, validationObject, showHugimSection } = props;
    const hugimFormSettings = useSelector(state => state.box.box.settings?.[HUGIM_FORM])
    const groupFormSettings = useSelector(state => state.box.box.settings?.[GROUP_FORM])
    const appointmentFormSettings = useSelector(state => state.box.box.settings?.[APPOINTMENT_FORM])
    const boxCategoriesByType = useSelector(state => state.schedule.eventTypes.data)

    const [hugimOptions, setHugimOptions] = useState(null);
    const [selectedHugim, setSelectedHugim] = useState(null);
    const [selectedAppointmentCategories, setSelectedAppointmentCategories] = useState(null);
    const [selectedGroupCategories, setSelectedGroupCategories] = useState(null);
    const disableHugimTrialCheckbox = useMemo(() => hugimFormSettings?.filter(set => set.id !== values.hugimType?.id)?.find(set => set.properties.allowTrial), [hugimFormSettings, values.hugimType?.id]);
    const disableGroupTrialCheckbox = useMemo(() => groupFormSettings?.filter(set => set.id !== values.groupType?.id)?.find(set => set.properties.allowTrial), [hugimFormSettings, values.groupType?.id]);
    const groupBoxCategoryOptions = useMemo(() => {
        if(boxCategoriesByType && boxCategoriesByType[CLASS]) {
            return boxCategoriesByType[CLASS].filter(item => !!item.active && item.membership_types?.length > 0)
                .map(item => {
                    const isDisabled = groupFormSettings?.filter(set => set.id !== values.groupType?.id)?.find(set => set.properties.box_categories.includes(item.id)) //if other setting row has already chosen this hug, then it is now disabled for any other form
                    return {...item,value: item.id, label: `${item.name} ${isDisabled ? t('category-disabled-select-option') : ''}`, isDisabled}
                })
        }
        return null
    }, [boxCategoriesByType, groupFormSettings, values.groupType?.id]);

    const appointmentBoxCategoryOptions = useMemo(() => {
        if (boxCategoriesByType && boxCategoriesByType[APPOINTMENT]) {
            return boxCategoriesByType[APPOINTMENT].filter(item => !!item.active && item.membership_types?.length > 0)
                .map(item => {
                    const isDisabled = appointmentFormSettings?.filter(set => set.id !== values.appointmentType?.id)?.find(set => set.properties.box_categories.includes(item.id))
                    return {...item, value: item.id, label: `${item.name} ${isDisabled ? t('category-disabled-select-option') : ''}`, isDisabled}
                })
        }
        return null
    }, [boxCategoriesByType, appointmentFormSettings, values.appointmentType?.id]);

    useEffect(() => {
        if(showHugimSection) {
            if (!hugimOptions) {
                fetchCourses()
            } else {
                if (!selectedHugim && values.hugimType?.id && values.hugimType?.hugim) { //aka init on edit existing setting row
                    const selected = hugimOptions.filter(item => values.hugimType.hugim.includes(item.id))
                    setSelectedHugim(selected)
                }
            }
        }
    }, [hugimOptions, showHugimSection]);

    useEffect(() => {
        if(groupBoxCategoryOptions && !selectedGroupCategories && values.groupType?.id && values.groupType?.box_categories) {
            const selected = groupBoxCategoryOptions.filter(item => values.groupType.box_categories.includes(item.id))
            setSelectedGroupCategories(selected)
        }
    }, [groupBoxCategoryOptions, values.groupType?.id]);

    useEffect(() => {
        if(appointmentBoxCategoryOptions && !selectedAppointmentCategories && values.appointmentType?.id && values.appointmentType?.box_categories) {
            const selected = appointmentBoxCategoryOptions.filter(item => values.appointmentType.box_categories.includes(item.id))
            setSelectedAppointmentCategories(selected)
        }
    }, [appointmentBoxCategoryOptions, values.appointmentType?.id]);

    useEffect(() => {
        if(selectedHugim) {
            setFieldValue('hugimType.hugim', selectedHugim.map(item => item.id))
        }
    }, [selectedHugim]);

    useEffect(() => {
        if(selectedGroupCategories) {
            setFieldValue('groupType.box_categories', selectedGroupCategories.map(item => item.id))
        }
    }, [selectedGroupCategories]);
    
    useEffect(() => {
        if(selectedAppointmentCategories) {
            setFieldValue('appointmentType.box_categories', selectedAppointmentCategories.map(item => item.id))
        }
    }, [selectedAppointmentCategories]);

    const fetchCourses = async () => {
        try {
            const res = await fetchApi('hugim/getList', 'GET', false, false, true);
            setHugimOptions(res.filter(item => item.status === 'active' && (!item.end_date || item.end_date >= moment().format(dbDateFormat))).map(item => {
                const isDisabled = hugimFormSettings?.filter(set => set.id !== values.hugimType?.id)?.find(set => set.properties.hugim.includes(item.id)) //if other setting row has already chosen this hug, then it is now disabled for any other form
                return {...item,value: item.id, label: `${item.series_name} ${isDisabled ? t('hug-disabled-select-option') : ''}`, isDisabled}
            }))
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <VStack space={'1.5rem'}>
            {showHugimSection &&
                <VStack {...sectionCardProps}>
                    {hugimOptions &&
                        <FormInput label={t('courses')+checkRequired('hugimType.hugim', validationObject)} width={'100%'} containerProps={{mb: touched?.hugimType?.hugim && errors?.hugimType?.hugim ? '1rem' : 0}} labelProps={{mb: '0.5rem'}} error={(touched?.hugimType?.hugim && errors?.hugimType?.hugim) ? errors.hugimType.hugim : null}>
                            <AdvanceSelect options={hugimOptions ?? []}
                                           initValue={selectedHugim}
                                           isClearable={true}
                                           isMulti={true}
                                           closeMenuOnSelect={false}
                                           allowSelectAll={true}
                                           onChange={(res) => setSelectedHugim(res)}
                                           isError={touched?.hugimType?.hugim && errors?.hugimType?.hugim}
                            />
                        </FormInput>}
                    <FormInput label={t('trial-form-checkbox-label')} width={'100%'} containerProps={{mb: 0}}>
                        <Tooltip title={disableHugimTrialCheckbox ? t('disabled-trial-checkbox-tooltip') : null}>
                            <CustomCheckbox text={t('trial-form-checkbox')} isChecked={values.hugimType?.allowTrial ?? false} onChange={(val) => setFieldValue('hugimType.allowTrial', val)} disabled={disableHugimTrialCheckbox} style={{width: 'fit-content', cursor: disableHugimTrialCheckbox ? 'not-allowed' : 'pointer'}}/>
                        </Tooltip>
                    </FormInput>
                </VStack>
            }
            {appointmentBoxCategoryOptions &&
                <VStack {...sectionCardProps}>
                    <FormInput label={t('appointmentLabelName')} infoTooltip={t('form-appointment-types-tooltip')} width={'100%'} containerProps={{mb: 0}} labelProps={{mb: '0.5rem'}}>
                        <AdvanceSelect options={appointmentBoxCategoryOptions ?? []}
                                       initValue={selectedAppointmentCategories}
                                       isClearable={true}
                                       isMulti={true}
                                       closeMenuOnSelect={false}
                                       allowSelectAll={true}
                                       onChange={(res) => setSelectedAppointmentCategories(res)}
                                       isError={touched?.appointmentType?.box_categories && errors?.appointmentType?.box_categories}
                        />
                    </FormInput>
                </VStack>
            }
            <VStack {...sectionCardProps}>
                {groupBoxCategoryOptions &&
                    <FormInput label={t('classLabelName')+checkRequired('groupType.box_categories', validationObject)} infoTooltip={t('form-group-types-tooltip')} width={'100%'} containerProps={{mb: touched?.groupType?.box_categories && errors?.groupType?.box_categories ? '1rem' : 0}} labelProps={{mb: '0.5rem'}} error={(touched?.groupType?.box_categories && errors?.groupType?.box_categories) ? errors.groupType.box_categories : null}>
                        <AdvanceSelect options={groupBoxCategoryOptions ?? []}
                                       initValue={selectedGroupCategories}
                                       isClearable={true}
                                       isMulti={true}
                                       closeMenuOnSelect={false}
                                       allowSelectAll={true}
                                       onChange={(res) => setSelectedGroupCategories(res)}
                                       isError={touched?.groupType?.box_categories && errors?.groupType?.box_categories}
                        />
                    </FormInput>}
                <FormInput label={t('trial-form-group-checkbox-label')} width={'100%'} containerProps={{mb: 0}}>
                    <Tooltip title={disableGroupTrialCheckbox ? t('disabled-trial-group-checkbox-tooltip') : null}>
                        <CustomCheckbox text={t('trial-form-group-checkbox')} isChecked={values.groupType?.allowTrial ?? false} onChange={(val) => setFieldValue('groupType.allowTrial', val)} disabled={disableGroupTrialCheckbox} style={{width: 'fit-content', cursor: disableGroupTrialCheckbox ? 'not-allowed' : 'pointer'}}/>
                    </Tooltip>
                </FormInput>
            </VStack>
        </VStack>
    );
};

export default FlowForm;